/**
 * Wither boss model (vanilla WitherBossModel, 64x64 sheet): shoulders bar, ribcage with three ribs
 * and a swaying spine tail, a centre head and two smaller side heads. Drawn at scale 2 about the
 * feet (shrinking to 1.5 at the start of the spawn sequence), with the white "invulnerable" sheet
 * flashing during the spawn and the faint blue wither-armor shell at half health.
 * `v.color`: bits 0-7 = spawn invulnerable ticks / 2, bit 8 = wither armor (entities/boss/wither.ts).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { MobView } from '../../game/types';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat } from './core';
import type { NetherAnim } from './netherModels';

const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0): CubeDef =>
  ({ tex, from, size, inflate });

const RIB_X = 0.20420352;
const TAIL_BASE_Y = 6.9;

interface Parts {
  root: THREE.Group;
  ribcage: THREE.Group;
  tail: THREE.Group;
  heads: THREE.Group[];
}

/** One copy of the whole model (the body, and again slightly inflated for the armor shell). */
function build(mat: EntityMat, g: number): Parts {
  const root = new THREE.Group();
  const part = (cubes: CubeDef[], pos: [number, number, number], rot?: [number, number, number]): THREE.Group => {
    const p = buildPart({ cubes, pos, rot }, 64, 64, mat);
    root.add(p);
    return p;
  };
  part([cube([0, 16], [-10, 3.9, -0.5], [20, 3, 3], g)], [0, 0, 0]);
  const ribcage = part([
    cube([0, 22], [0, 0, 0], [3, 10, 3], g),
    cube([24, 22], [-4, 1.5, 0.5], [11, 2, 2], g),
    cube([24, 22], [-4, 4, 0.5], [11, 2, 2], g),
    cube([24, 22], [-4, 6.5, 0.5], [11, 2, 2], g),
  ], [-2, TAIL_BASE_Y, -0.5], [RIB_X, 0, 0]);
  const tail = part([cube([12, 22], [0, 0, 0], [3, 6, 3], g)],
    [-2, TAIL_BASE_Y + Math.cos(RIB_X) * 10, -0.5 + Math.sin(RIB_X) * 10], [0.83252215, 0, 0]);
  const heads = [
    part([cube([0, 0], [-4, -4, -4], [8, 8, 8], g)], [0, 0, 0]),
    part([cube([32, 0], [-4, -4, -4], [6, 6, 6], g)], [-8, 4, 0]),
    part([cube([32, 0], [-4, -4, -4], [6, 6, 6], g)], [10, 4, 0]),
  ];
  return { root, ribcage, tail, heads };
}

export function witherAnim(env: EnvUniforms): NetherAnim {
  const texNormal = sheetTexture('entity/wither/wither');
  const texFlash = sheetTexture('entity/wither/wither_invulnerable');
  const mat = entityMaterial(env, texNormal);
  // wither armor: additive, unlit, dark blue (vanilla draws the swirl texture at 0.5 alpha-ish over the body)
  const shellMat = entityMaterial(env, sheetTexture('entity/wither/wither_armor'), { emissive: true });
  shellMat.transparent = true;
  shellMat.depthWrite = false;
  shellMat.blending = THREE.AdditiveBlending;

  const body = build(mat, 0);
  const shell = build(shellMat, 0.5);
  const parts = new THREE.Group();
  parts.add(body.root, shell.root);

  return {
    parts, mats: [mat],
    animate(v: MobView, _q, headY, headX, age) {
      const invul = (v.color & 255) * 2;
      const armored = (v.color & 256) !== 0;
      // WitherBossRenderer: scale 2, minus (invul / 220) * 0.5 while spawning; about the feet (model y = 24 px)
      const s = invul > 0 ? 2 - (invul / 220) * 0.5 : 2;
      parts.scale.setScalar(s);
      parts.position.y = 24 * (1 - s);
      // the white sheet flashes through the last 80 ticks (and stays white before that)
      mat.uniforms.uMap.value = invul > 0 && (invul > 80 || Math.floor(invul / 5) % 2 !== 1) ? texFlash : texNormal;
      shell.root.visible = armored;
      shellMat.uniforms.uTint.value.setScalar(0.11 + 0.04 * Math.sin(age * 0.2)); // a faint shimmer, not a glow

      // WitherBossModel.setupAnim
      const f = Math.cos(age * 0.1);
      const rx = (0.065 + 0.05 * f) * Math.PI;
      for (const set of [body, shell]) {
        set.ribcage.rotation.x = rx;
        set.tail.position.set(-2, TAIL_BASE_Y + Math.cos(rx) * 10, -0.5 + Math.sin(rx) * 10);
        set.tail.rotation.x = (0.265 + 0.1 * f) * Math.PI;
        // centre head follows the look direction; the side heads wander around it (they track their own targets)
        set.heads[0].rotation.set(headX, headY, 0);
        set.heads[1].rotation.set(headX * 0.8, headY + 0.25 * Math.sin(age * 0.07 + 1), 0);
        set.heads[2].rotation.set(headX * 0.8, headY - 0.25 * Math.sin(age * 0.06), 0);
      }
    },
    dispose() { mat.dispose(); shellMat.dispose(); },
  };
}
